/**
 * BackOrderItemRow — a single back-order line (shared by the full page and the mini offcanvas).
 * Quantity is uncapped (back order = demand beyond stock); editing auto-persists via onSetQuantity.
 * Layout mirrors the cart's CartItemRow: on mobile (isMobile) the per-line price metadata stacks
 * above the quantity inputs instead of sitting to the left.
 */
import { useMemo } from "react";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import QuantityInput from "../cart/QuantityInput";
import { formatCurrency } from "../../utils/formatters";
import ImageComingSoon from "../common/ImageComingSoon";

const BackOrderItemRow = ({ item, onSetQuantity, isUpdating, isMobile = false }) => {
  const productData = {
    id: item.productSkuVariantID,
    unitsPerCase: item.productSkuVariant?.unitsPerCase || 1,
    optSellByTypeID: item.productSkuVariant?.optSellByTypeID || 1,
    // No unitAvailable → the quantity input is uncapped.
    unitPrice: item.productSkuVariant?.unitPrice || item.unitPrice || 0,
    singleUnitFee: item.singleUnitFee || item.productSkuVariant?.singleUnitFee || 0,
    productSkuVariantMOQPricing: item.productSkuVariant?.productSkuVariantMOQPricing || [],
  };

  const calculator = useMemo(
    () => new ProductQuantityCalculator(productData),
    [item.productSkuVariantID, productData.unitsPerCase, productData.optSellByTypeID],
  );

  // Uncapped input: strip unitAvailable so the quantity isn't clamped to stock.
  const inputProduct = {
    id: productData.id,
    unitsPerCase: productData.unitsPerCase,
    optSellByTypeID: productData.optSellByTypeID,
  };

  const display = calculator.unitsToDisplay(item.unitQuantity || 0);
  const priceInfo = calculator.calculatePrice(item.unitQuantity || 0);

  const setTotal = (cases, units) =>
    onSetQuantity(item.productSkuVariantID, calculator.displayToUnits(cases, units), productData);

  const name = item.productSkuVariant?.name || "Unknown Product";
  const flavor = item.productSkuVariant?.flavor;
  const brandName = item.productSkuVariant?.brand?.name;
  const imageUrl =
    item.productSkuVariant?.images?.[0]?.fullPath ||
    (item.productSkuVariant?.productSKUVariantImages?.[0]?.image?.fullPath
      ? `https://stashhouseimages.blob.core.windows.net/${item.productSkuVariant.productSKUVariantImages[0].image.fullPath}`
      : null);

  const caseLabel = calculator.caseLabel?.toLowerCase() || "case";
  const unitLabelPlural = calculator.unitLabelPlural?.toLowerCase() || "units";

  return (
    <tr className="cart-item-row border-dark-subtle">
      {/* Product Image */}
      <td>
        <div
          className="ratio ratio-1x1 rounded overflow-hidden bg-light"
          style={{ width: "48px", height: "48px" }}
        >
          {imageUrl ? (
            <img src={imageUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <ImageComingSoon style={{ minHeight: 40 }} />
          )}
        </div>
      </td>

      {/* Product info */}
      <td>
        <div className="fw-medium">{name}</div>
        {brandName && <small className="text-primary d-block">{brandName}</small>}
        {flavor && <small className="text-muted d-block">{flavor}</small>}
        <small className="text-muted d-block">
          {productData.unitsPerCase} {unitLabelPlural} / {caseLabel}
        </small>
      </td>

      {/* Price + Quantity — single unified cell (stacks on mobile) */}
      <td>
        <div className="d-flex flex-column gap-2 mx-auto mx-md-0">
          {/* === Cases Group === */}
          {calculator.canSellByCase && (
            <div className={`d-flex ${isMobile ? "flex-column " : ""}gap-1`}>
              <div
                className={isMobile ? "mb-1" : "text-end pe-2"}
                style={{ minWidth: isMobile ? "auto" : "160px" }}
              >
                <small className="text-stash-accent" style={{ lineHeight: 1 }}>
                  <strong>{formatCurrency(priceInfo.unitPrice * productData.unitsPerCase)}</strong> / {caseLabel}
                  <span className="text-black-50 d-block" style={{ fontSize: "0.75em", lineHeight: 1 }}>
                    ({productData.unitsPerCase} {unitLabelPlural} / {caseLabel})
                  </span>
                </small>
              </div>
              <QuantityInput
                value={display.cases}
                onChange={(cases) => setTotal(cases, display.units)}
                quantityType="cases"
                product={inputProduct}
                currentUnitQuantity={display.units}
                size="sm"
                disabled={isUpdating}
                isUpdating={isUpdating}
                label={calculator.caseLabelPlural?.toLowerCase() || "cases"}
              />
            </div>
          )}

          {/* === Units Group === */}
          {calculator.canSellByUnit && productData.unitsPerCase > 1 && (
            <div className={`d-flex ${isMobile ? "flex-column " : ""}gap-1`}>
              <div
                className={isMobile ? "mb-1" : "text-end pe-2"}
                style={{ minWidth: isMobile ? "auto" : "160px" }}
              >
                <small className="text-stash-accent" style={{ lineHeight: 1 }}>
                  <strong>{formatCurrency(priceInfo.unitPrice)}</strong> / {calculator.unitLabel?.toLowerCase() || "unit"}
                </small>
              </div>
              <QuantityInput
                value={display.units}
                onChange={(units) => setTotal(display.cases, units)}
                quantityType="units"
                product={inputProduct}
                currentCaseQuantity={display.cases}
                size="sm"
                disabled={isUpdating}
                isUpdating={isUpdating}
                label={unitLabelPlural}
              />
            </div>
          )}
        </div>
      </td>

      {/* Line Total */}
      <td>
        <div className="text-end">
          <small className="text-muted d-block">
            {formatCurrency(priceInfo.unitPrice)} × {item.unitQuantity}
          </small>
          <strong className="text-stash-accent">{formatCurrency(priceInfo.grandTotal)}</strong>
        </div>
      </td>

      {/* Actions */}
      <td className="text-center">
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => onSetQuantity(item.productSkuVariantID, 0, productData)}
          disabled={isUpdating}
          title="Remove from back order"
        >
          <i className="bi bi-trash"></i>
        </button>
      </td>
    </tr>
  );
};

export default BackOrderItemRow;
